import { useCallback, useState } from 'react';
import { ApiAction, ApiError, FetchStatus } from './types';
import { DEFAULT_HEADERS } from './constants';
import { buildQueryParameters } from './utilities';
import settings from '../../config';
import { useAuth0 } from '@auth0/auth0-react';

const useHttp = () => {
    const [fetchStatus, setFetchStatus] = useState<FetchStatus>('NotStarted');
    const [statusCode, setStatusCode] = useState<number | null>(null);
    const [error, setError] = useState<ApiError | null>(null);

    const { getAccessTokenSilently } = useAuth0();

    const sendRequest = useCallback(async <T>(action: ApiAction<T>) => {

        setFetchStatus('Fetching');
        setError(null);

        let url = action.nonNcassEndpoint || action.url.startsWith(settings.API_URL) ? action.url : `${settings.API_URL}${action.url}`;
        let headers = action.headers ? action.headers : DEFAULT_HEADERS;        

        if (!!action.queryParameters) url += buildQueryParameters(action.queryParameters);

        if (!action.nonNcassEndpoint) {
            const token = await getAccessTokenSilently();
            headers = {...headers, Authorization: `Bearer ${token}`}
        }

        try {
            const response = await fetch(url, {
                method: action.method ?? 'GET',
                headers,
                body: action.requestBody ?? null,
            });

            if (response.status) setStatusCode(response.status);

            if (!response.ok) {
                const error = {
                    message: response.statusText ?? 'Unknown error occurred',
                    statusCode: response.status,
                    response,
                }
                setError(error);
                setFetchStatus('Error');
                if (action?.onError)
                    action?.onError(error);
                return;
            }

            const text = await response.text();
            const data: T = text ? JSON.parse(text) : null;
            action.onComplete(data);
        } catch (err) {
            console.error('useHttp error:', err);
            setError({ message: (err as any).message ?? 'Unknown error occurred' });
        }
        setFetchStatus('Done');
    }, []);

    return {
        fetchStatus,
        error,
        sendRequest,
        statusCode,
    };
};

export default useHttp;